import { DataButton, InputCheckbox } from "@sellar-io/components";
import { TableInputContainer } from "../../../components/tableInputContainer/TableInputContainer";
import { twMerge } from "tailwind-merge";
import { useState } from "react";
import {
  ProductStatusType,
  useStockOverviewStore,
} from "../StockOverviewStore";
import { useShallow } from "zustand/react/shallow";
import { InputSelect, InputOption } from "@sellar-io/components";
import { StockOverviewContainerType } from "@sellar-io/api-v2/types";
import { getProductDisplayNameByKey } from "@sellar-io/common";

const stockOverviewFilterContainerTypesList: StockOverviewContainerType[] = [
  "bottle",
  "can",
  "keg",
  "cask",
  "bagInBox",
  "pouch",
  "glass",
  "bag",
  "jar",
  "box",
  "badge",
] as const;

const isValidContainerType = (
  value: string,
): value is StockOverviewContainerType =>
  stockOverviewFilterContainerTypesList.includes(value);

export const StockOverviewFilter = ({
  onDismiss,
  isMobile,
}: {
  onDismiss: () => void;
  isMobile?: boolean;
}) => {
  const state = useStockOverviewStore(useShallow((state) => state));
  const [containerType, setContainerType] =
    useState<StockOverviewContainerType | null>(state.containerType);
  const [productStatus, setProductStatus] = useState<ProductStatusType>(
    state.productStatus,
  );

  const [preOrder, setPreOrder] = useState(state.preOrder);
  const [inStock, setInStock] = useState(state.inStock);

  const filtersApplied =
    state.containerType !== null ||
    state.preOrder === true ||
    state.inStock === true ||
    state.search.length ||
    productStatus !== null;

  const disabled =
    containerType === state.containerType &&
    preOrder === state.preOrder &&
    inStock === state.inStock &&
    productStatus === state.productStatus;

  const onApply = (reset: boolean) => {
    if (reset) {
      useStockOverviewStore.getState().reset();
    } else {
      useStockOverviewStore.getState().setState({
        ...state,
        containerType,
        preOrder,
        inStock,
        productStatus,
      });
    }
    onDismiss();
  };

  return (
    <div>
      <div
        className={twMerge(
          "w-full",
          isMobile ? "px-4 pt-2" : "border-b border-zinc-300 p-4",
        )}
      >
        <span className="font-semibold">Filter stock</span>
      </div>
      <div className="w-full p-4">
        <div className="flex flex-col gap-3">
          {/* product state filter */}
          <div className="flex w-full items-center justify-between gap-3">
            <div className="flex shrink-0">Product status</div>
            <div className="w-[55%]">
              <TableInputContainer>
                <InputSelect
                  defaultValue={state.productStatus ?? undefined}
                  onChange={(e) => {
                    const value = e.target.value;
                    const productStatus = (
                      value.length ? value : null
                    ) as ProductStatusType;
                    setProductStatus(productStatus);
                  }}
                  className="text-sm font-medium"
                >
                  <InputOption value={""}>Any</InputOption>
                  <InputOption value={"active"}>Active</InputOption>
                  <InputOption value={"inactive"}>Inactive</InputOption>
                </InputSelect>
              </TableInputContainer>
            </div>
          </div>

          {/* container type filter */}

          <div className="flex w-full items-center justify-between gap-3">
            <div className="flex shrink-0">Container</div>
            <div className="w-[55%]">
              <TableInputContainer>
                <InputSelect
                  defaultValue={state.containerType ?? undefined}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (isValidContainerType(value)) {
                      setContainerType(value);
                    }
                  }}
                  className="text-sm font-medium"
                >
                  <InputOption value={""}>All containers</InputOption>
                  {stockOverviewFilterContainerTypesList.map((item) => (
                    <InputOption key={item} value={item}>
                      {item === "badge"
                        ? "Badge"
                        : getProductDisplayNameByKey(item)}
                    </InputOption>
                  ))}
                </InputSelect>
              </TableInputContainer>
            </div>
          </div>

          {/* pre-order filter */}

          <div className="flex w-full items-center justify-between gap-3">
            <div className="flex shrink-0">Pre-order</div>
            <div className="w-[55%]">
              <InputCheckbox
                checked={preOrder}
                onChange={(e) => setPreOrder(e.target.checked)}
                className="text-sm font-medium"
              />
            </div>
          </div>

          {/* In stock filter */}

          <div className="flex w-full items-center justify-between gap-3">
            <span>In stock</span>
            <div className="w-[55%]">
              <InputCheckbox
                checked={inStock}
                onChange={(e) => setInStock(e.target.checked)}
                className="text-sm font-medium"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex w-full items-center gap-2 p-4">
        <DataButton
          onClick={() => onApply(false)}
          border={true}
          disabled={disabled}
        >
          {"Apply"}
        </DataButton>
        {!!filtersApplied && (
          <DataButton color="transparent" onClick={() => onApply(true)}>
            Clear all
          </DataButton>
        )}
      </div>
    </div>
  );
};
